<template>
  <!-- Historique projet -->
  <div
    v-if="project.statut != 'BROUILLON'"
    class="onlyDisplayedifSectionIsActive"
    :class="{ activeSection: section == 'historique' }"
  >
    <div class="projet-part">
      <ProjetHistoryView
        :role="role"
        :projet-annuel="project"
        :is-porteur-projet="isPorteurProjet"
      ></ProjetHistoryView>
    </div>
  </div>
  <div class="onlyDisplayedifSectionIsActive" :class="{ activeSection: section != 'historique' }">
    <ProjetInscriptionVueEnsemble
      :projet-annuel="projetInscription"
      :validation-activated="validationActivated"
      :show-validation-errors="showValidationErrors"
      :readonly="readonly || !isPorteurProjet"
      @dirtify-project="$emit('dirtify-project')"
      @trigger-validation="$emit('trigger-validation')"
      @projet-annuel-updated="updateProjetAnnuel"
    />
    <ProjetParticipants
      :projet-annuel="project"
      :readonly="readonly"
      :is-porteur-projet="isPorteurProjet"
      :validation-activated="validationActivated"
      :show-validation-errors="showValidationErrors"
      @dirtify-project="$emit('dirtify-project', $event)"
      @trigger-validation="$emit('trigger-validation')"
      @projet-annuel-updated="updateProjetAnnuel"
    />
    <ProjetInscriptionSite
      :projet-annuel="projetInscription"
      :validation-activated="validationActivated"
      :show-validation-errors="showValidationErrors"
      :readonly="readonly || !isPorteurProjet"
      @dirtify-project="$emit('dirtify-project')"
      @trigger-validation="$emit('trigger-validation')"
      @projet-annuel-updated="updateProjetAnnuel"
    />
    <ProjetFinancementView
      :projet-annuel="project"
      :validation-activated="validationActivated"
      :readonly="readonly || !isPorteurProjet"
      @dirtify-project="$emit('dirtify-project')"
      @trigger-validation="$emit('trigger-validation')"
      @projet-annuel-updated="updateProjetAnnuel"
    />

    <ProjetInscriptionFinalisation
      :validation-activated="validationActivated"
      :projet-annuel="projetInscription"
      :show-validation-errors="showValidationErrors"
      :readonly="readonly || !isPorteurProjet"
      :projet-validation="lastProjectValidation"
      @dirtify-project="$emit('dirtify-project')"
      @trigger-validation="$emit('trigger-validation')"
      @projet-annuel-updated="updateProjetAnnuel"
    />
  </div>
</template>

<script lang="ts">
import { defineComponent, PropType } from "vue"
import { eventBus } from "@/services/EventBus"
import { ProjetAnnuelDTO, ProjetInscriptionDTO } from "@/model/bean/GeneratedDTOs"
import { ProjetAnnualValidation } from "@/pages/projet-page/commons/ProjectValidationAndSaveHandler.vue"
import { ValidationStatus } from "@/model/ValidationResult"
import {
  BackToPreviousScreenItem,
  BulletWithValidation,
  ContextMenu,
  NavbarItem,
  NavbarItemWithoutLink,
} from "@/views/menus/MenuItems"

import ProjetParticipants from "@/pages/projet-page/info-administratives/ProjetParticipants.vue"
import ProjetInscriptionVueEnsemble from "@/pages/projet-page/inscription/ProjetInscriptionVueEnsemble.vue"
import ProjetInscriptionSite from "@/pages/projet-page/inscription/ProjetInscriptionSite.vue"
import ProjetInscriptionFinalisation from "@/pages/projet-page/inscription/ProjetInscriptionFinalisation.vue"
import ProjetFinancementView from "@/pages/projet-page/info-administratives/ProjetFinancementView.vue"
import ProjetHistoryView from "../history/ProjetHistoryView.vue"

export const PROJECT_SECTIONS = {
  HISTORY: "historique",
  INSCRIPTION: "inscription",
}

export default defineComponent({
  name: "ProjetInscriptionHolder",
  components: {
    ProjetParticipants,
    ProjetInscriptionVueEnsemble,
    ProjetInscriptionFinalisation,
    ProjetInscriptionSite,
    ProjetFinancementView,
    ProjetHistoryView,
  },
  props: {
    project: {
      type: Object as PropType<ProjetAnnuelDTO>,
      required: true,
    },
    role: {
      type: String,
      default: "",
    },
    section: {
      type: String,
      default: "inscription",
    },
    activeTab: {
      type: Number,
      default: 0,
    },
    validationActivated: {
      type: Boolean,
      default: true,
    },
    showValidationErrors: {
      type: Boolean,
      default: true,
    },
    lastProjectValidation: {
      type: Object as PropType<ProjetAnnualValidation>,
      required: false,
      default: null,
    },
    readonly: {
      type: Boolean,
      required: false,
      default: false,
    },
    isPorteurProjet: {
      type: Boolean,
      required: false,
      default: false,
    },
    currentScrollY: {
      type: Number,
      required: false,
      default: 0,
    },
    pxFromBottom: {
      type: Number,
      required: false,
      default: 0,
    },
  },

  emits: ["dirtify-project", "trigger-validation", "projet-annuel-updated"],
  data() {
    return {
      anchorsToTrack: [] as string[],
      // Indicates if projet has been validated at least once
      // (and hence lastProjectvalidationCanBeUsed to display last validation results)
      wasValidatedAtLeastOnce: false,
    }
  },

  computed: {
    projetInscription(): ProjetInscriptionDTO {
      return this.project as ProjetInscriptionDTO
    },
  },

  watch: {
    lastProjectValidation() {
      this.wasValidatedAtLeastOnce = true
      this.refreshContextMenu()
    },
    validationActivated() {
      this.refreshContextMenu()
    },
    currentScrollY() {
      this.updateActiveAnchor()
    },
  },
  created(): void {
    if (this.$route.params["section"] == "bref") {
      this.pushNewRouteWithCorrectSection("inscription")
    }
  },
  mounted(): void {
    this.refreshContextMenu()

    // Launch a validation at first load
    this.$emit("trigger-validation")
  },
  methods: {
    refreshContextMenu(): void {
      const menuItems = []
      this.anchorsToTrack = []
      if (this.project) {
        menuItems.push(new BackToPreviousScreenItem())
        menuItems.push(new NavbarItemWithoutLink(this.project.nom, ""))
        if (this.project.statut != "BROUILLON") {
          menuItems.push(this.getMenuEntry(PROJECT_SECTIONS.HISTORY, "calendar", "0"))
        } else {
          this.pushNewRouteWithCorrectSection("inscription")
        }
        const inscriptionEntry = this.getMenuEntry(
          PROJECT_SECTIONS.INSCRIPTION,
          "calendar-alt",
          "0",
        )
        inscriptionEntry.children = [
          this.getBulletEntry(PROJECT_SECTIONS.INSCRIPTION, "ensemble"),
          this.getBulletEntry(PROJECT_SECTIONS.INSCRIPTION, "participants"),
          this.getBulletEntry(PROJECT_SECTIONS.INSCRIPTION, "site"),
          this.getBulletEntry(PROJECT_SECTIONS.INSCRIPTION, "financements"),
          this.getBulletEntry(PROJECT_SECTIONS.INSCRIPTION, "finalisation"),
        ]
        menuItems.push(inscriptionEntry)

        const possibleMenuParents = [""]
        eventBus.emit("contextual-menu-changed", new ContextMenu(possibleMenuParents, menuItems))
        this.updateActiveAnchor()
      }
    },

    getMenuEntry(section: string, icone: string, subtab: string): NavbarItem {
      const basePath = "/project/" + this.project.projetId + "/" + this.project.id + "/"
      return new NavbarItem(
        basePath + section + "/" + subtab,
        this.$t("projet.menu." + section).toString(),
        icone,
        false,
      )
    },

    getBulletEntry(section: string, anchor: string): BulletWithValidation {
      let validationStatus = ValidationStatus.NONE
      if (this.validationActivated && this.wasValidatedAtLeastOnce && this.lastProjectValidation) {
        validationStatus = ValidationStatus.VALID
        if (this.lastProjectValidation.invalidFields.get(anchor)) {
          validationStatus = ValidationStatus.ERROR
        } else if (this.lastProjectValidation.emptyFields.get(anchor)) {
          validationStatus = ValidationStatus.WARNING
        }
      }

      // For the 'finalisation section': get the worse status
      if (anchor == "finalisation") {
        if (this.lastProjectValidation.invalidFields.size > 0) {
          validationStatus = ValidationStatus.ERROR
        } else if (this.lastProjectValidation.emptyFields.size > 0) {
          validationStatus = ValidationStatus.WARNING
        }
      }

      this.anchorsToTrack.push(anchor)
      return new BulletWithValidation(
        anchor,
        this.$t("projet." + section + "." + anchor).toString(),
        validationStatus,
      )
    },

    updateActiveAnchor(): void {
      for (let i = 0; i < this.anchorsToTrack.length; i++) {
        const anchorY = document.getElementById(this.anchorsToTrack[i])?.offsetTop
        if (this.pxFromBottom <= 60) {
          eventBus.emit(
            "contextual-menu-anchor-update",
            this.anchorsToTrack[this.anchorsToTrack.length - 1],
          )
          return
        } else {
          if (anchorY && this.currentScrollY <= anchorY - 20) {
            eventBus.emit("contextual-menu-anchor-update", this.anchorsToTrack[Math.max(0, i - 1)])
            return
          }
        }
      }
      eventBus.emit(
        "contextual-menu-anchor-update",
        this.anchorsToTrack[this.anchorsToTrack.length - 1],
      )
    },

    updateProjetAnnuel(updatedProjetAnnuel: any): void {
      this.$emit("projet-annuel-updated", updatedProjetAnnuel)
      this.$emit("dirtify-project")
    },

    pushNewRouteWithCorrectSection(section: string): void {
      const newRouteWithCorrectSection = { ...this.$router.currentRoute.value }
      newRouteWithCorrectSection.params = {
        ...newRouteWithCorrectSection.params,
        section: section,
      }
      this.$router.push(newRouteWithCorrectSection)
    },
  },
})
</script>
